import {Menu, MenuGroup, MenuHeading} from '@/components/ui/menu-atoms/MenuAtoms'
import {StyledSelectArrow, StyledSelectTrigger} from '@/components/ui/select-atoms/style'
import {MenuButton, MenuItemProps, MenuProvider} from '@ariakit/react'
import {forwardRef, Fragment, useRef, useEffect, useState, ChangeEvent} from 'react'
import {useTranslation} from '@/translations/i18n'
import {ChevronDownIcon, ChevronLeftIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {StyledMenuItem} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useDiseases} from '../../services/useDiseases'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText'
import {Skeleton} from '@components/ui/skeleton/Skeleton'
import {patientServiceTypeMapToLabel, debounce} from '@/utilities/helpers'
import {DiseaseExtended} from '../../types'
import {InputText} from '@/components/commons/input-text/InputText'

export const DiagnosisFilter = ({value, onChange}: {value: string[]; onChange: (value: string[]) => void}) => {
    const {t} = useTranslation()
    const [searchValue, setSearchValue] = useState('')
    const diseasesQuery = useDiseases({limit: 100, responseType: 'extended', search: searchValue})

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value)
    }, 1000)

    return (
        <MenuProvider placement="bottom-end">
            <MenuButton
                render={
                    <StyledSelectTrigger
                        $hasChildren={value.length >= 1}
                        type="button"
                        variant="secondary"
                        $hasError={false}
                    />
                }
            >
                {value.length == 0 ? t('table_filters:select_value') : t('commons:x_selected', {count: value.length})}
                <StyledSelectArrow $isOpen={false} render={<ChevronDownIcon />} />
            </MenuButton>
            <DiagnosisMenu>
                <MenuGroup>
                    <InputText
                        type="text"
                        onChange={onSearch}
                        placeholder={t('commons:search_placeholder')}
                        autoFocus
                    />
                </MenuGroup>
                {diseasesQuery.isLoading ? (
                    <MenuGroup>
                        <Flexbox direction="column" gap={1}>
                            {Array.from({length: 6}, (_, index) => (
                                <Skeleton key={index} height={32} />
                            ))}
                        </Flexbox>
                    </MenuGroup>
                ) : diseasesQuery.isError ? (
                    <MenuGroup>
                        <InputHelpText error={t('errors:default')} />
                    </MenuGroup>
                ) : diseasesQuery.remappedData.length == 0 ? (
                    <MenuGroup>
                        <InputHelpText helpText={t('commons:no_results')} />
                    </MenuGroup>
                ) : (
                    diseasesQuery.remappedData.map(disease => (
                        <DiagnosisMenuItem
                            diseasesChildren={disease.children}
                            key={disease.id}
                            value={value}
                            onChangeValue={onChange}
                            disease={disease}
                        >
                            {disease.name} - {t(patientServiceTypeMapToLabel[disease.serviceType])}
                        </DiagnosisMenuItem>
                    ))
                )}
            </DiagnosisMenu>
        </MenuProvider>
    )
}

const DiagnosisMenu = ({children}: {children?: React.ReactNode}) => {
    const {zIndex} = useTheme()
    return (
        <Menu style={{zIndex: zIndex.dropdown + 1}} width="400px" unmountOnHide hideOnHoverOutside={false}>
            <MenuGroup>{children}</MenuGroup>
        </Menu>
    )
}

interface DiagnosisMenuItemProps extends MenuItemProps {
    value: string[]
    onChangeValue: (value: string[]) => void
    disease: DiseaseExtended
    diseasesChildren?: DiseaseExtended[]
}

const hasAnyChildSelected = (value: string[], children?: DiseaseExtended[]): boolean => {
    if (!children?.length) return false
    return children.some(child => value.includes(child.id) || hasAnyChildSelected(value, child.children))
}

const getAllChildIds = (children?: DiseaseExtended[]): string[] => {
    if (!children?.length) return []
    return children.reduce((acc, child) => {
        return [...acc, child.id, ...getAllChildIds(child.children)]
    }, [] as string[])
}

const DiagnosisMenuItem = forwardRef<HTMLDivElement, DiagnosisMenuItemProps>(
    ({value, onChangeValue, disease, diseasesChildren, ...props}, ref) => {
        const isChecked = value.includes(disease.id)
        const isIndeterminate = !isChecked && hasAnyChildSelected(value, diseasesChildren)
        const checkboxRef = useRef<HTMLInputElement>(null)

        useEffect(() => {
            if (checkboxRef.current) {
                checkboxRef.current.indeterminate = isIndeterminate
            }
        }, [isIndeterminate])

        //if the checkbox is checked or indeterminate, remove the disease and all its children from the value, otherwise add the disease and all its children to the value
        const onToggleChange = () => {
            if (isChecked || isIndeterminate) {
                const allChildIds = getAllChildIds(diseasesChildren)
                onChangeValue(value.filter(id => id !== disease.id && !allChildIds.includes(id)))
            } else {
                const allChildIds = getAllChildIds(diseasesChildren)
                onChangeValue([...value, disease.id, ...allChildIds])
            }
        }

        const MenuTrigger = diseasesChildren?.length ? MenuButton : Fragment

        return (
            <MenuProvider placement="left">
                <MenuTrigger showOnHover={false}>
                    <StyledMenuItem ref={ref} {...props} hideOnClick={false}>
                        {diseasesChildren?.length ? <ChevronLeftIcon /> : <span style={{width: 16, flexShrink: 0}} />}
                        <Checkbox
                            id={`diagnosis-checkbox-${disease.id}`}
                            checked={isChecked || isIndeterminate}
                            onChange={onToggleChange}
                            ref={checkboxRef}
                        />
                        {props.children}
                    </StyledMenuItem>
                </MenuTrigger>
                <DiagnosisMenu>
                    <MenuHeading>{props.children}</MenuHeading>
                    {diseasesChildren?.map(child => (
                        <DiagnosisMenuItem
                            diseasesChildren={child.children}
                            key={child.id}
                            value={value}
                            onChangeValue={onChangeValue}
                            disease={child}
                        >
                            {child.name}
                        </DiagnosisMenuItem>
                    ))}
                </DiagnosisMenu>
            </MenuProvider>
        )
    }
)
